import { useAtom, useSetAtom } from 'jotai';
import { Box, Eraser, Hammer, Maximize2, Paintbrush, Sofa, Sparkles, Sun } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  additionalInfoAtom,
  desiredStyleSettingsAtom,
  goodWeatherDaylightAtom,
  goodWeatherSeasonAtom,
  roomModeSettingsAtom,
  roomTypeSettingsAtom,
  settingsChangedAtom,
} from '../../atoms/settings';
import {
  type ROOM_MODE_OPTION_VALUE,
  ROOM_MODE_OPTIONS,
  roomModeOptions,
} from '../../constants/room-mode';
import { SettingsSectionHeader } from './settings-section-header';

const modeIcons = {
  furnishEmptyRoom: Sofa,
  changeStyle: Paintbrush,
  cleanRoom: Sparkles,
  furnitureRemoval: Eraser,
  finishConstruction: Hammer,
  changeFloor: Paintbrush,
  improveImageQuality: Maximize2,
  goodWeather: Sun,
  enhanceAndUpscale: Maximize2,
  photorealisticFrom3D: Box,
};

export const RoomModeSettings = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [roomMode, setRoomMode] = useAtom(roomModeSettingsAtom);
  const setAdditionalInfo = useSetAtom(additionalInfoAtom);
  const setDesiredStyle = useSetAtom(desiredStyleSettingsAtom);
  const setGoodWeatherDaylight = useSetAtom(goodWeatherDaylightAtom);
  const setGoodWeatherSeason = useSetAtom(goodWeatherSeasonAtom);
  const setRoomType = useSetAtom(roomTypeSettingsAtom);
  const setSettingsChanged = useSetAtom(settingsChangedAtom);

  const handleSelect = (mode: ROOM_MODE_OPTION_VALUE) => {
    if (mode === roomMode) return;

    setRoomMode(mode);
    setAdditionalInfo('');

    if (mode === ROOM_MODE_OPTIONS.GOOD_WEATHER.value) {
      setDesiredStyle(undefined);
      setRoomType(undefined);
    } else {
      setGoodWeatherDaylight(undefined);
      setGoodWeatherSeason(undefined);
    }

    setSettingsChanged(true);
  };

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-sm border border-slate-200 bg-white sm:w-[287px]'
      )}
    >
      <SettingsSectionHeader
        label={t('mode')}
        required
        value={t(roomMode)}
        isOpen={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen ? (
        <div className="grid grid-cols-4 gap-2 px-3 pb-3">
          {roomModeOptions.map(({ value: modeOption }) => {
            const Icon = modeIcons[modeOption.value];
            const isSelected = modeOption.value === roomMode;

            return (
              <Tooltip key={modeOption.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t(modeOption.value)}
                    onClick={() => handleSelect(modeOption.value)}
                    className={cn(
                      'flex h-[72px] w-full min-w-0 flex-col items-center justify-center gap-1 rounded-sm border py-2 text-center text-[9px] font-semibold leading-[13px] transition-colors',
                      isSelected
                        ? 'border-primary-light bg-primary-light/10 text-primary-light shadow-none'
                        : 'border-slate-200 bg-[#f0f2f580] text-slate-500 hover:border-primary-light'
                    )}
                  >
                    <Icon aria-hidden className="size-4 shrink-0" />
                    <span className="w-full whitespace-normal text-[9px] leading-[12px] [overflow-wrap:normal] [word-break:normal]">
                      {t(modeOption.value)}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  sideOffset={8}
                  className="max-w-[190px] rounded-sm border border-slate-200 px-3 py-2 text-center text-sm font-medium leading-5 text-slate-700 shadow-md"
                >
                  {t(modeOption.description)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
